import { coreGet, coreMutate } from "../../core-api/api-client";

export interface TeamSummary {
  id: string;
  code: string;
  name: string;
  workspaceId: string;
  description: string;
  createdAt: string;
  status: "ACTIVE" | "ARCHIVED";
  version: number;
  caseCount: number;
  activeMemberCount: number;
}

export interface TeamRole {
  id: string;
  code: string;
  name: string;
  status: string;
}

export interface TeamDirectoryUser {
  id: string;
  displayName: string;
  username: string | null;
  primaryEmail: string | null;
  status: string;
  roles: TeamRole[];
  teams: Array<Pick<TeamSummary, "id" | "code" | "name" | "status">>;
}

export interface TeamMember {
  id: string;
  userId: string;
  status: "ACTIVE" | "SUSPENDED" | "REVOKED";
  version: number;
  validFrom?: string;
  validTo?: string | null;
  user?: Omit<TeamDirectoryUser, "teams"> & {
    workspaceMembershipStatus: string;
  };
}

export interface TeamCase {
  id: string;
  caseNumber: string;
  subject: string;
  businessOutcome: string;
  status: string;
  currentProgress: string | null;
  rowVersion: number;
  updatedAt: string;
}

export interface TeamCasePage {
  items: TeamCase[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TeamCapabilities {
  manageTeams: boolean;
  readMembers: boolean;
  manageMembers: boolean;
}
export const loadTeams = (workspaceId: string, signal?: AbortSignal) =>
  coreGet<{ items: TeamSummary[]; capabilities: TeamCapabilities }>(
    "/teams",
    signal,
    workspaceId,
  );
export const loadTeam = (
  id: string,
  workspaceId: string,
  signal?: AbortSignal,
) => coreGet<TeamSummary>(`/teams/${id}`, signal, workspaceId);
export const loadTeamMembers = (
  id: string,
  workspaceId: string,
  signal?: AbortSignal,
) =>
  coreGet<{ items: TeamMember[] }>(`/teams/${id}/members`, signal, workspaceId);
export const loadTeamUsers = (workspaceId: string, signal?: AbortSignal) =>
  coreGet<{ items: TeamDirectoryUser[] }>(
    "/teams/directory-users",
    signal,
    workspaceId,
  );
export const loadTeamCases = (id: string, page = 1) =>
  coreGet<TeamCasePage>(`/teams/${id}/cases?page=${page}&pageSize=20`);

export const createTeam = (
  input: {
    name: string;
    description: string;
    memberUserIds?: string[];
    reason: string;
  },
  workspaceId: string,
) => coreMutate<TeamSummary>("/teams", "POST", input, undefined, workspaceId);
export const updateTeam = (
  id: string,
  input: {
    expectedVersion: number;
    name?: string;
    description?: string;
    memberUserIds?: string[];
    status?: "ACTIVE";
    reason: string;
  },
  workspaceId: string,
) =>
  coreMutate<TeamSummary>(
    `/teams/${id}`,
    "PATCH",
    input,
    undefined,
    workspaceId,
  );
export const archiveTeam = (
  id: string,
  expectedVersion: number,
  reason: string,
  workspaceId: string,
) =>
  coreMutate<TeamSummary>(
    `/teams/${id}`,
    "DELETE",
    {
      expectedVersion,
      reason,
    },
    undefined,
    workspaceId,
  );
export const addTeamMember = (teamId: string, userId: string, reason: string) =>
  coreMutate<TeamMember>(`/teams/${teamId}/members`, "POST", {
    userId,
    membershipRole: "MEMBER",
    reason,
  });
export const removeTeamMember = (membership: TeamMember, reason: string) =>
  coreMutate<TeamMember>(`/team-memberships/${membership.id}`, "PATCH", {
    expectedVersion: membership.version,
    status: "REVOKED",
    reason,
  });
export const unlinkTeamCase = (
  teamId: string,
  item: TeamCase,
  reason: string,
) =>
  coreMutate<TeamCase>(`/teams/${teamId}/cases/${item.id}/unlink`, "POST", {
    expectedVersion: item.rowVersion,
    reason,
  });
